/**
 * @file assetsAndPermissions.tsx
 * @module utils/assetsAndPermissions
 * @description Shared image asset registries and role-permission trees used by UTrade Admin components and business workflows.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { PermissionAndModuleName } from 'src/types';
import {
  Add,
  AllPermissions,
  Delete,
  Download,
  Edit,
  View,
  Dispatch,
  EditBypassPermission,
  LoadingBypassPermission,
  AddressEditPermission,
  OrderCancelPermission,
  ViewRoutePermission,
  CancelDeliveryPlanPermission,
  DownloadGRDocumentPermission,
  DownloadOrderDocumentPermission,
  UtradeDiscountPermission,
  PermissionModule,
  Permissions,
  ChangeLoadingManagerPermission,
  ChangeDriverPermission,
} from './constants';

/** Inward (UProcure) entry in the role editor grid; shared by both role-permission trees. */
export const inwardRolePermissionModule = {
  module: 'Inward',
  value: PermissionModule.Inward,
  // Inward-specific permissions need subModulePermission: true, otherwise the role editor
  // (getSubmodulePermissionTypes) only shows All / View / Add / Edit / Delete.
  permissions: [
    { title: 'All', value: AllPermissions },
    { title: 'View', value: View },
    // Inward Add only records received quantities per SKU; stock goes live after approvals
    { title: 'Add (save quantities, stock not live)', value: Add },
    { title: 'Edit (Add New Batch)', value: Edit },
    {
      title: 'Download (end-of-day inward report)',
      value: Permissions.Download,
      subModulePermission: true,
    },
    {
      title: 'Loading Bay Manager (does GRN when assigned a PO)',
      value: Permissions.InwardLoadingBayManager,
      subModulePermission: true,
    },
    {
      title: 'Assign Loading Bay Manager, Dock & Inward Date',
      value: Permissions.InwardAssignLoadingBay,
      subModulePermission: true,
    },
    {
      title: 'Warehouse Approval (verify with photo)',
      value: Permissions.InwardWarehouseApprove,
      subModulePermission: true,
    },
    {
      title: 'Finance Approval (invoice, receipt, documents; takes stock live)',
      value: Permissions.InwardFinanceApprove,
      subModulePermission: true,
    },
    {
      title: 'Procurement: Acknowledge Cost Price & Update Selling Price',
      value: Permissions.InwardAcknowledgeCostPrice,
      subModulePermission: true,
    },
    {
      title: 'Verify EFD receipt (tick "EFD verified" in the report)',
      value: Permissions.InwardVerifyEfd,
      subModulePermission: true,
    },
    {
      title: 'Reject GRN (quantities go back to the PO for a new GRN)',
      value: Permissions.InwardRejectGrn,
      subModulePermission: true,
    },
  ],
};

export const IMAGES = {
  // DUMMY ICONS/IMAGES ********************************
  // Brands
  WadsworthDistributorsLtd: require('../_admin/assets/media/brand/wadsworth-distributors-limited.png'),
  ElvenAgri: require('../_admin/assets/media/brand/elven_agri.png'),
  SuperMealsLtd: require('../_admin/assets/media/brand/super_meal.png'),
  CoolBlue: require('../_admin/assets/media/brand/cool_blue.png'),
  MacLeansBeneCIBOLtd: require('../_admin/assets/media/brand/macLeans.png'),
  Afribon: require('../_admin/assets/media/brand/afribon.png'),
  KilimoFreshFoodsAfricaLTD: require('../_admin/assets/media/brand/kilimo.png'),
  ZamzamStationerySupermarket: require('../_admin/assets/media/brand/zamzam.png'),
  NilePerchFisheriesLtd: require('../_admin/assets/media/brand/nile_perch.png'),
  OmrosFoods: require('../_admin/assets/media/brand/omros_foods.png'),
  AmazonMonstaPops: require('../_admin/assets/media/product/amazon-monsta-pops-strawberry.png'),
  AmazonRollGum: require('../_admin/assets/media/product/amazon-roll-gum.png'),
  AzamWheatFlour: require('../_admin/assets/media/product/azam-wheat-flour.png'),
  NutroWafarBiscuits: require('../_admin/assets/media/product/nutro-wafer-biscuits.png'),
  LatoMilkPowder: require('../_admin/assets/media/product/lato-milk-powder-400g.png'),
  // PERMANENT ICONS/IMAGES ********************************
  // Products
  AmazonMonstaPopsStrawberry: require('../_admin/assets/media/product/amazon-monsta-pops-strawberry.png'),
  // SVGs **permanent**
  ThreeDot: require('../_admin/assets/media/svg_uTrade/three-dot.svg').default,
  ThreeDotRounded: require('../_admin/assets/media/svg_uTrade/three-dot-round.svg'),
  verifiedBadge: require('../_admin/assets/media/svg_uTrade/verified.svg')
    .default,
  eyeBlue: require('../_admin/assets/media/svg_uTrade/eye-active.png'),
  eyeGray: require('../_admin/assets/media/svg_uTrade/eye.png'),
  SellerSVG: require('../_admin/assets/media/svg_uTrade/sellers.svg').default,
  CustomerSVG: require('../_admin/assets/media/svg_uTrade/customers.svg')
    .default,
  OrderSVG: require('../_admin/assets/media/svg_uTrade/orders.svg').default,
  PaymentSVG: require('../_admin/assets/media/svg_uTrade/payments.svg').default,
  ProductSVG: require('../_admin/assets/media/svg_uTrade/products.svg').default,
  GreenDot: require('../_admin/assets/media/svg_uTrade/green_dot.svg').default,
  YellowError: require('../_admin/assets/media/svg_uTrade/error-warning.svg')
    .default,
  PartialTruckOne:
    require('../_admin/assets/media/svg_uTrade/partial_truck_1.svg').default,
  ArrowUp: require('../_admin/assets/media/svg_uTrade/up-arrow.svg').default,
  ArrowDown: require('../_admin/assets/media/svg_uTrade/arrow-down.svg')
    .default,
  PartialTruckTwo:
    require('../_admin/assets/media/svg_uTrade/partial_truck_2.svg').default,
  downArrow: require('../_admin/assets/media/svg_uTrade/down-arrow.svg')
    .default,
  fastDelivery: require('../_admin/assets/media/svg_uTrade/fast-delivery.svg')
    .default,
};
export const BannerIMAGES = {
  BannerAd1: require('../_admin/assets/media/advertising/b1.webp'),
  BannerAd2: require('../_admin/assets/media/advertising/b2.webp'),
  BannerAd3: require('../_admin/assets/media/advertising/b3.webp'),
  BannerAd4: require('../_admin/assets/media/advertising/b4.webp'),
  BannerAd5: require('../_admin/assets/media/advertising/b5.webp'),
};
export const rolePermissions = [
  {
    module: 'All Modules',
    value: PermissionModule.AllModules,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Dashboard',
    value: PermissionModule.Dashboard,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
    ],
  },
  {
    module: 'Reports',
    value: PermissionModule.Reports,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Download', value: Download },
    ],
    submodules: [
      {
        module: 'Customer Purchase Behavior Analysis',
        value: PermissionModule.CustomerPurchaseBehaviourReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Geographic Sales Insights Report',
        value: PermissionModule.GeographicSalesInsightReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Area-Based Customer Segmentation',
        value: PermissionModule.AreaBasedCustomerSegmentationReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Frequent Customer Purchase Pattern Analysis Report',
        value: PermissionModule.FrequentCustomerPurchasePatternAnalysisReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Product Sales Performance Report',
        value: PermissionModule.ProductSalesPerformanceReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Brand-Wise Sales Performance Report',
        value: PermissionModule.BrandWiseSalesPerformanceReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Category and Sub-Category Sales Analysis Report',
        value: PermissionModule.CategoryAndSubCategoryAnalysisReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Revenue Generation Report',
        value: PermissionModule.RevenueGenerationReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Commission Analysis Report',
        value: PermissionModule.CommissionAnalysisReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Product Variant Inventory Status Report',
        value: PermissionModule.InventoryStatusReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
    ],
  },
  {
    module: 'Sellers',
    value: PermissionModule.Sellers,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      {
        module: 'All Sellers',
        value: PermissionModule.AllSeller,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'KYC Verification',
        value: PermissionModule.KycVerifications,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Pending Certificate',
        value: PermissionModule.PendingCertificate,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Payment to Sellers',
        value: PermissionModule.PaymentToSellers,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Sales Report',
        value: PermissionModule.SellersSalesReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
    ],
  },
  {
    module: 'Customers',
    value: PermissionModule.Customers,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      { title: 'Delete', value: Delete },
      { title: 'Download', value: Download },
    ],
    submodules: [
      {
        module: 'All Customers',
        value: PermissionModule.AllCustomers,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Refunds to Customers',
        value: PermissionModule.RefundsToCustomer,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Receivable Reports',
        value: PermissionModule.ReceivableReports,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Customer Ledger',
        value: PermissionModule.CustomerLedger,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: "Add", value: Add },
          // { title: 'Edit', value: Edit },
          // { title: "Delete", value: Delete },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Driver Ledger',
        value: PermissionModule.DriverLedger,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
          // { title: "Delete", value: Delete },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Pre Order Customer',
        value: PermissionModule.PreOrderCustomerList,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
    ],
  },
  {
    module: 'UProcure',
    value: PermissionModule.UProcure,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Download', value: Download },
      { title: 'Edit', value: Edit },
      // { title: "Delete", value: Delete },
    ],
    submodules: [
      {
        module: 'Dashboard',
        value: PermissionModule.UProcureDashboard,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: 'Category',
        value: PermissionModule.Categories,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Brands',
        value: PermissionModule.Brand,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Technical details',
        value: PermissionModule.TechnicalDetails,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Product variants',
        value: PermissionModule.ProductVariants,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Vendors',
        value: PermissionModule.AllVendors,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Assign Product to Vendors',
        value: PermissionModule.AssignProductToVendors,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Payment to Vendors',
        value: PermissionModule.PaymentToVendors,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Inventory Replenishment Report',
        value: PermissionModule.InventoryReplenishmentReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Purchase Orders List',
        value: PermissionModule.POManagement,
        permissions: [
          { title: 'All', value: Permissions.AllPermissions },
          { title: 'View', value: Permissions.View },
          { title: 'Add', value: Permissions.Add },
          { title: 'Download', value: Permissions.Download },
          {
            title: 'Delivery Date Update',
            value: Permissions.PO_DELIVERY_DATE_UPDATE,
            subModulePermission: true,
          },
          {
            title: 'Create GRN',
            value: Permissions.PO_CREATE_GRN,
            subModulePermission: true,
          },
          {
            title: 'Approve and Reject PO',
            value: Permissions.PO_APPROVE_AND_REJECT,
            subModulePermission: true,
          },
          {
            title: 'Save All',
            value: Permissions.PO_SAVE_ALL,
            subModulePermission: true,
          },
          {
            title: 'Pending Tab',
            value: Permissions.Pending_Tab,
            subModulePermission: true,
          },
          {
            title: 'Approved Tab',
            value: Permissions.Approved_Tab,
            subModulePermission: true,
          },
          {
            title: 'In Process Tab',
            value: Permissions.In_Process_Tab,
            subModulePermission: true,
          },
          {
            title: 'Completed Tab',
            value: Permissions.Completed_Tab,
            subModulePermission: true,
          },
          {
            title: 'Rejected Tab',
            value: Permissions.Rejected_Tab,
            subModulePermission: true,
          },
          {
            title: 'Cancelled Tab',
            value: Permissions.Cancelled_Tab,
            subModulePermission: true,
          },
          {
            title: 'Draft PO Tab',
            value: Permissions.Draft_PO_Tab,
            subModulePermission: true,
          },
          {
            title: 'Edit Draft PO',
            value: Permissions.Draft_PO_Edit,
            subModulePermission: true,
          },
          {
            title: 'Delete Draft PO',
            value: Permissions.Draft_PO_Delete,
            subModulePermission: true,
          },
          {
            title: 'Submit Draft PO below tonnage',
            value: Permissions.Draft_PO_Bypass_Tonnage,
            subModulePermission: true,
          },
          {
            title: 'Submit Draft PO below MOQ',
            value: Permissions.Draft_PO_Bypass_MOQ,
            subModulePermission: true,
          },
          {
            title: 'Edit supplier PO tonnage (Product Catalog)',
            value: Permissions.Supplier_Tonnage_Edit,
            subModulePermission: true,
          },
          {
            title: 'Edit supplier MOQ (Product Catalog)',
            value: Permissions.Supplier_MOQ_Edit,
            subModulePermission: true,
          },
          {
            title: 'Edit supplier groups (Vendor Profile > Groups)',
            value: Permissions.Supplier_Groups_Edit,
            subModulePermission: true,
          },
        ],
      },
      {
        module: 'Goods Received Note',
        value: PermissionModule.GoodsReceivedNote,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add (save & Add to inventory)', value: Add },
          { title: 'Edit (Add New Batch)', value: Edit },
        ],
      },
      {
        module: 'Escalations',
        value: PermissionModule.Escalations,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: 'Vendor Ledger',
        value: PermissionModule.VendorLedger,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      inwardRolePermissionModule,
    ],
  },
  {
    module: 'Finance Reports',
    value: PermissionModule.FinanceReports,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      {
        module: 'Sales Reports',
        value: PermissionModule.FinanceSalesReportPermission,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Orders Reports',
        value: PermissionModule.FinanceOrderReportPermission,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Delivery Reports',
        value: PermissionModule.FinanceDeliveryReportPermission,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Return Reports',
        value: PermissionModule.FinanceReturnReportPermission,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Outward Reports',
        value: PermissionModule.OutWardReports,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: 'Return Product Reports',
        value: PermissionModule.ReturnProductReports,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: PermissionAndModuleName[PermissionModule.DispatchReport].moduleName,
        value: PermissionModule.DispatchReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: PermissionAndModuleName[PermissionModule.DispatchReportOnlyReturnConsidered].moduleName,
        value: PermissionModule.DispatchReportOnlyReturnConsidered,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: PermissionAndModuleName[PermissionModule.DispatchReportWithReturnedCombined].moduleName,
        value: PermissionModule.DispatchReportWithReturnedCombined,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
    ],
  },
  {
    module: 'Orders & delivery',
    value: PermissionModule.OrdersDelivery,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      // { title: 'Delete', value: Delete },
      // { title: 'Download', value: Download },
      { title: 'Dispatch', value: Dispatch },
      { title: 'Load Bypass', value: LoadingBypassPermission },
      { title: 'Edit Order', value: EditBypassPermission },
      { title: 'Address Edit', value: AddressEditPermission },
      { title: 'Order Cancel', value: OrderCancelPermission },
      { title: 'Utrade Discount', value: UtradeDiscountPermission },
      {
        title: 'Restrict Order View',
        value: Permissions.RestrictOrderView,
      },
      { title: 'View Route', value: Permissions.ViewRoute },
      { title: 'Cancel Delivery Plan', value: CancelDeliveryPlanPermission },
      { title: 'Download GR', value: DownloadGRDocumentPermission },
      {
        title: 'Download Order Doc',
        value: DownloadOrderDocumentPermission,
      },
      { title: 'Change Driver', value: Permissions.ChangeDriverPermission },
      { title: 'Change Loading Manager', value: Permissions.ChangeLoadingManagerPermission },
      { title: "Mark Goods As Loaded", value: Permissions.OrdersDelivery_Mark_goods_as_loaded },
      { title: "Mark As Out For Delivery", value: Permissions.OrdersDelivery_Mark_as_out_for_delivery }
    ],
  },
  {
    module: 'Plan Delivery Routes',
    value: PermissionModule.RoutePlan,
    permissions: [
      { title: 'All', value: AllPermissions },
      // { title: "View / List", value: View },
      { title: 'Add', value: Add },
      // { title: "Edit", value: Edit },
      // { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Outstanding Report',
    value: PermissionModule.OutstandingReport,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      // { title: "Add", value: Add },
      // { title: "Edit", value: Edit },
      // { title: "Delete", value: Delete },
    ],
  },
  {
    module: 'Pending Delivery Orders',
    value: PermissionModule.PendingDeliveryOrder,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      // { title: "Edit", value: Edit },
      // { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Master',
    value: PermissionModule.Master,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      { title: 'Delete', value: Delete },
    ],
    submodules: [
      {
        module: 'Business types',
        value: PermissionModule.BusinessType,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Business certificates',
        value: PermissionModule.BusinessCertificates,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Master Products',
        value: PermissionModule.MasterProduct,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Drivers',
        value: PermissionModule.Driver,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Vehicles',
        value: PermissionModule.Vehicle,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Vehicle loading areas',
        value: PermissionModule.VehicleLoadingArea,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          // { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Seller product zone',
        value: PermissionModule.SellerProductZone,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
          // { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Roles & Permissions',
        value: PermissionModule.RolesAndPermission,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'User Management',
        value: PermissionModule.UserManagement,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'District & City',
        value: PermissionModule.DistrictAndCity,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
    ],
  },
  {
    module: 'Inventory',
    value: PermissionModule.Inventory,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      {
        module: 'Goods in Warehouse',
        value: PermissionModule.GoodsWarehouse,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Out of Stock',
        value: PermissionModule.OutOfStockList,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: "Add", value: Add },
          // { title: 'Edit', value: Edit },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Low stock list',
        value: PermissionModule.LowStockLists,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Goods requests',
        value: PermissionModule.GoodsRequest,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Received direct goods',
        value: PermissionModule.ReceivedDirectGood,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Return Requests',
        value: PermissionModule.ProductReturnRequests,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Inventory Report',
        value: PermissionModule.InventoryReportPermission,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
    ],
  },
  {
    module: 'Warehouse',
    value: PermissionModule.WarehouseModule,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      // {
      //   module: "Warehouse",
      //   PermissionModule.WarehouseManger,
      //   permissions: [
      //     { title: "All", value: AllPermissions },
      //     { title: "View / List", value: View },
      //     { title: "Add", value: Add },
      //     { title: 'Edit', value: Edit },
      //   ],
      // },
      {
        module: 'Inter Transfer Request',
        value: PermissionModule.WarehouseInterTransfer,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
    ],
  },
  {
    module: 'Inter Transfer Request (UMart)',
    value: PermissionModule.InterTransferUmart,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      // { title: 'Edit', value: Edit },
      // { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Customer service',
    value: PermissionModule.CustomerServices,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'All Products',
    value: PermissionModule.All_Products,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      // { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Price Update Request (Approval)',
    value: PermissionModule.PriceUpdateRequest,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      // { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      // { title: "Delete", value: Delete },
    ],
  },
  {
    module: 'Platform fees',
    value: PermissionModule.PlatformFees,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      // { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      // { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Advertising',
    value: PermissionModule.Advertising,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      // { title: 'Edit', value: Edit },
      // { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Contact inquiries',
    value: PermissionModule.ContactInquires,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
    ],
  },
  {
    module: 'Custom Notification',
    value: PermissionModule.CustomNotifications,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
    ],
  },
  {
    module: 'General settings',
    value: PermissionModule.Settings,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      {
        module: 'Privacy Policy',
        value: PermissionModule.Privacy_Policy,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Terms & Conditions',
        value: PermissionModule.TermsConditions,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'App Settings',
        value: PermissionModule.AppSettings,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
    ],
  },
  {
    module: 'Price Catalog',
    value: PermissionModule.PriceCatalog,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      {
        title: 'Critical Alerts',
        value: Permissions.PC_CriticalAlerts,
      },
      {
        title: 'Change History',
        value: Permissions.PC_ChangeHistory,
      },
      {
        title: 'Edit Cost Price',
        value: Permissions.PC_EditCostPrice,
      },
      {
        title: 'Edit Selling Price',
        value: Permissions.PC_EditSellingPrice,
      },
      {
        title: 'Edit Tax',
        value: Permissions.PC_EditTax,
      },
      {
        title: 'Edit Days Of Inventory Holding',
        value: Permissions.PC_EditDaysOfInventoryHolding,
      },
      {
        title: 'Draft PO Enabled',
        value: Permissions.PC_EditDraftPOenabled,
      },
      {
        title: 'Apply Edit On All Warehouses',
        value: Permissions.PC_All_Warehouses,
      },
    ],
  },
];
/** @deprecated Warehouse Manager no longer gets an exclusive permission tree — `rolePermissions` is the single permission bridge for every role. Will be removed. */
export const rolePermissionsForWarehouseManger = [
  // {
  //   module: "All Modules",
  //   PermissionModule.AllModules,
  //   permissions: [
  //     { title: "All", value: AllPermissions },
  //     { title: "View / List", value: View },
  //     { title: "Add", value: Add },
  //     { title: "Edit", value: Edit },
  //     { title: "Delete", value: Delete },
  //   ],
  // },
  {
    module: 'Dashboard',
    value: PermissionModule.Dashboard,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
    ],
  },

  {
    module: 'Customers',
    value: PermissionModule.Customers,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      {
        module: 'All Customers',
        value: PermissionModule.AllCustomers,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Refunds to Customers',
        value: PermissionModule.RefundsToCustomer,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Receivable Reports',
        value: PermissionModule.ReceivableReports,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Customer Ledger',
        value: PermissionModule.CustomerLedger,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Driver Ledger',
        value: PermissionModule.DriverLedger,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Pre Order Customer',
        value: PermissionModule.PreOrderCustomerList,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
    ],
  },

  {
    module: 'Orders & delivery',
    value: PermissionModule.OrdersDelivery,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      // { title: 'Delete', value: Delete },
      // { title: 'Download', value: Download },
      { title: 'Dispatch', value: Dispatch },
      { title: 'Load Bypass', value: LoadingBypassPermission },
      { title: 'Edit Order', value: EditBypassPermission },
      { title: 'Address Edit', value: AddressEditPermission },
      { title: 'Order Cancel', value: OrderCancelPermission },
      { title: 'Utrade Discount', value: UtradeDiscountPermission },
      {
        title: 'Restrict Order View',
        value: Permissions.RestrictOrderView,
      },
      { title: 'View Route', value: ViewRoutePermission },
      { title: 'Cancel Delivery Plan', value: CancelDeliveryPlanPermission },
      { title: 'Download GR', value: DownloadGRDocumentPermission },
      {
        title: 'Download Order Doc',
        value: DownloadOrderDocumentPermission,
      },
      { title: 'Change Driver', value: ChangeDriverPermission },
      { title: 'Change Loading Manager', value: ChangeLoadingManagerPermission },
      { title: "Mark Goods As Loaded", value: Permissions.OrdersDelivery_Mark_goods_as_loaded },
      { title: "Mark As Out For Delivery", value: Permissions.OrdersDelivery_Mark_as_out_for_delivery }
    ],
  },
  {
    module: 'Plan Delivery Routes',
    value: PermissionModule.RoutePlan,
    permissions: [
      { title: 'All', value: AllPermissions },
      // { title: "View / List", value: View },
      { title: 'Add', value: Add },
      // { title: "Edit", value: Edit },
      // { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Outstanding Report',
    value: PermissionModule.OutstandingReport,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      // { title: "Add", value: Add },
      // { title: "Edit", value: Edit },
      // { title: "Delete", value: Delete },
    ],
  },
  {
    module: 'Pending Delivery Orders',
    value: PermissionModule.PendingDeliveryOrder,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      // { title: "Edit", value: Edit },
      // { title: 'Delete', value: Delete },
    ],
  },
  {
    module: 'Master',
    value: PermissionModule.Master,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      { title: 'Delete', value: Delete },
    ],
    submodules: [
      {
        module: 'Drivers',
        value: PermissionModule.Driver,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Vehicles',
        value: PermissionModule.Vehicle,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Vehicle loading areas',
        value: PermissionModule.VehicleLoadingArea,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          // { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Seller product zone',
        value: PermissionModule.SellerProductZone,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
          // { title: 'Delete', value: Delete },
        ],
      },
      // {
      //   module: 'Finance members',
      //   PermissionModule.FinanceMember,
      //   permissions: [
      //     { title: 'All', value: AllPermissions },
      //     { title: 'View', value: View },
      //     { title: 'Add', value: Add },
      //     { title: 'Edit', value: Edit },
      //     // { title: 'Delete', value: Delete },
      //   ],
      // },
    ],
  },
  {
    module: 'Inventory',
    value: PermissionModule.Inventory,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      {
        module: 'Goods in Warehouse',
        value: PermissionModule.GoodsWarehouse,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          // { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Low stock list',
        value: PermissionModule.LowStockLists,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          // { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Receieved direct goods',
        value: PermissionModule.ReceivedDirectGood,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Return Requests',
        value: PermissionModule.ProductReturnRequests,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Edit', value: Edit },
        ],
      },
      {
        module: 'Inventory Report',
        value: PermissionModule.InventoryReportPermission,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
    ],
  },

  {
    module: 'Warehouse',
    value: PermissionModule.WarehouseModule,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
    ],
    submodules: [
      // {
      //   module: "Warehouse",
      //   PermissionModule.WarehouseManger,
      //   permissions: [
      //     { title: "All", value: AllPermissions },
      //     { title: "View / List", value: View },
      //     { title: "Add", value: Add },
      //     { title: 'Edit', value: Edit },
      //   ],
      // },
      {
        module: 'Inter Transfer Request',
        value: PermissionModule.WarehouseInterTransfer,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
        ],
      },
    ],
  },
  {
    module: 'All Products',
    value: PermissionModule.All_Products,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      // { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      // { title: "Delete", value: Delete },
    ],
  },
  {
    module: 'Price Update Request (Approval)',
    value: PermissionModule.PriceUpdateRequest,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      // { title: 'Add', value: Add },
      { title: 'Edit', value: Edit },
      // { title: "Delete", value: Delete },
    ],
  },
  {
    module: 'UProcure',
    value: PermissionModule.UProcure,
    permissions: [
      { title: 'All', value: AllPermissions },
      { title: 'View', value: View },
      { title: 'Add', value: Add },
      { title: 'Download', value: Download },
      { title: 'Edit', value: Edit },
      // { title: "Delete", value: Delete },
    ],
    submodules: [
      {
        module: 'Dashboard',
        value: PermissionModule.UProcureDashboard,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: 'Category',
        value: PermissionModule.Categories,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Brands',
        value: PermissionModule.Brand,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Technical details',
        value: PermissionModule.TechnicalDetails,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Product variants',
        value: PermissionModule.ProductVariants,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Vendors',
        value: PermissionModule.AllVendors,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Assign Product to Vendors',
        value: PermissionModule.AssignProductToVendors,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Edit', value: Edit },
          { title: 'Delete', value: Delete },
        ],
      },
      {
        module: 'Payment to Vendors',
        value: PermissionModule.PaymentToVendors,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add', value: Add },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Inventory Replenishment Report',
        value: PermissionModule.InventoryReplenishmentReport,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Download', value: Download },
        ],
      },
      {
        module: 'Purchase Orders List',
        value: PermissionModule.POManagement,
        permissions: [
          { title: 'All', value: Permissions.AllPermissions },
          { title: 'View', value: Permissions.View },
          { title: 'Add', value: Permissions.Add },
          { title: 'Download', value: Permissions.Download },
          {
            title: 'Delivery Date Update',
            value: Permissions.PO_DELIVERY_DATE_UPDATE,
            subModulePermission: true,
          },
          {
            title: 'Create GRN',
            value: Permissions.PO_CREATE_GRN,
            subModulePermission: true,
          },
          {
            title: 'Approve and Reject PO',
            value: Permissions.PO_APPROVE_AND_REJECT,
            subModulePermission: true,
          },
          {
            title: 'Save All',
            value: Permissions.PO_SAVE_ALL,
            subModulePermission: true,
          },
          {
            title: 'Pending Tab',
            value: Permissions.Pending_Tab,
            subModulePermission: true,
          },
          {
            title: 'Approved Tab',
            value: Permissions.Approved_Tab,
            subModulePermission: true,
          },
          {
            title: 'In Process Tab',
            value: Permissions.In_Process_Tab,
            subModulePermission: true,
          },
          {
            title: 'Completed Tab',
            value: Permissions.Completed_Tab,
            subModulePermission: true,
          },
          {
            title: 'Rejected Tab',
            value: Permissions.Rejected_Tab,
            subModulePermission: true,
          },
          {
            title: 'Cancelled Tab',
            value: Permissions.Cancelled_Tab,
            subModulePermission: true,
          },
          {
            title: 'Draft PO Tab',
            value: Permissions.Draft_PO_Tab,
            subModulePermission: true,
          },
          {
            title: 'Edit Draft PO',
            value: Permissions.Draft_PO_Edit,
            subModulePermission: true,
          },
          {
            title: 'Delete Draft PO',
            value: Permissions.Draft_PO_Delete,
            subModulePermission: true,
          },
          {
            title: 'Submit Draft PO below tonnage',
            value: Permissions.Draft_PO_Bypass_Tonnage,
            subModulePermission: true,
          },
          {
            title: 'Submit Draft PO below MOQ',
            value: Permissions.Draft_PO_Bypass_MOQ,
            subModulePermission: true,
          },
          {
            title: 'Edit supplier PO tonnage (Product Catalog)',
            value: Permissions.Supplier_Tonnage_Edit,
            subModulePermission: true,
          },
          {
            title: 'Edit supplier MOQ (Product Catalog)',
            value: Permissions.Supplier_MOQ_Edit,
            subModulePermission: true,
          },
          {
            title: 'Edit supplier groups (Vendor Profile > Groups)',
            value: Permissions.Supplier_Groups_Edit,
            subModulePermission: true,
          },
        ],
      },
      {
        module: 'Goods Received Note',
        value: PermissionModule.GoodsReceivedNote,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
          { title: 'Add (save & Add to inventory)', value: Add },
          { title: 'Edit (Add New Batch)', value: Edit },
        ],
      },
      {
        module: 'Escalations',
        value: PermissionModule.Escalations,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      {
        module: 'Vendor Ledger',
        value: PermissionModule.VendorLedger,
        permissions: [
          { title: 'All', value: AllPermissions },
          { title: 'View', value: View },
        ],
      },
      inwardRolePermissionModule,
    ],
  },
];
/** @deprecated Dummy sample data, not backed by an API — will be removed. */
export const ordersJSON = [
  {
    orderDate: '25/03/23',
    orderTime: '21:30',
    orderId: '#00982',
    buyerDetails: 'Rashid Rabi Shamoon',
    quantity: '23',
    totalAmount: '237,500',
    status: 1,
    totalVehicles: '10',
    totalGoodsWeight: '9.7',
  },
  {
    orderDate: '25/03/23',
    orderTime: '15:10',
    orderId: '#00981',
    buyerDetails: 'Sulaiman Fareed',
    quantity: '12',
    totalAmount: '311,500',
    status: 2,
    totalVehicles: '13',
    totalGoodsWeight: '13.9',
  },
  {
    orderDate: '25/03/23',
    orderTime: '10:42',
    orderId: '#00980',
    buyerDetails: 'Bakri Hilmi Asghar',
    quantity: '23',
    totalAmount: '0',
    status: 3,
    totalVehicles: '15',
    totalGoodsWeight: '16',
  },
  {
    orderDate: '24/03/23',
    orderTime: '18:10',
    orderId: '#00979',
    buyerDetails: 'Mahjub Abboud',
    quantity: '27',
    totalAmount: '213,500',
    status: 4,
    totalVehicles: '9',
    totalGoodsWeight: '12.6',
  },
  {
    orderDate: '25/03/23',
    orderTime: ' 21:30',
    orderId: '#00982',
    buyerDetails: 'Jabbar Hud Handal',
    quantity: '19',
    totalAmount: '311,500',
    status: 2,
    totalVehicles: '22',
    totalGoodsWeight: '24.8',
  },
];
/** @deprecated Dummy sample data, not backed by an API — will be removed. */
export const BrandAndTotalSales = [
  {
    brandImage: IMAGES.WadsworthDistributorsLtd,
    brandName: 'Wadsworth Distributors Limited',
    totalSales: '10,555,500',
    commissionEarned: '1,794,435',
    paymentsCompleted: '7,236,500',
    paymentsDue: '3,319,000',
    totalOrders: '56',
    totalSale: '5.85M',
  },
  {
    brandImage: IMAGES.ElvenAgri,
    brandName: 'Elven Agri',
    totalSales: '9,513,214',
    commissionEarned: '1,426,982',
    paymentsCompleted: '5,520,047',
    paymentsDue: '3,993,167',
    totalOrders: '43',
    totalSale: '4.49M',
  },
  {
    brandImage: IMAGES.SuperMealsLtd,
    brandName: 'Super Meals Limited',
    totalSales: '7,767,500',
    commissionEarned: '932,100',
    paymentsCompleted: '5,500,531',
    paymentsDue: '2,266,969',
    totalOrders: '37',
    totalSale: '3.75M',
  },
  {
    brandImage: IMAGES.CoolBlue,
    brandName: 'Cool Blue',
    totalSales: '5,450,384',
    commissionEarned: '545,038',
    paymentsCompleted: '5,230,072',
    paymentsDue: '220,312',
    totalOrders: '32',
    totalSale: '2.65M',
  },
  {
    brandImage: IMAGES.MacLeansBeneCIBOLtd,
    brandName: 'MacLeans BeneCIBO Limited',
    totalSales: '2,050,020',
    commissionEarned: '266,502',
    paymentsCompleted: '1,750,047',
    paymentsDue: '299,973',
    totalOrders: '30',
    totalSale: '2.42M',
  },
  {
    brandImage: IMAGES.Afribon,
    brandName: 'Afribon',
    totalSales: '1,137,982',
    commissionEarned: '91,038',
    paymentsCompleted: '955,321',
    paymentsDue: '182,661',
    totalOrders: '28',
    totalSale: '2.29M',
  },
  {
    brandImage: IMAGES.KilimoFreshFoodsAfricaLTD,
    brandName: 'Kilimo Fresh Foods Africa LTD',
    totalSales: '982,217',
    commissionEarned: '88,399',
    paymentsCompleted: '0',
    paymentsDue: '982,217',
    totalOrders: '43',
    totalSale: '2.00M',
  },
  {
    brandImage: IMAGES.ZamzamStationerySupermarket,
    brandName: 'Zamzam Stationery Supermarket',
    totalSales: '750,490',
    commissionEarned: '112,573',
    paymentsCompleted: '45,212',
    paymentsDue: '45,212',
    totalOrders: '30',
    totalSale: '1.98M',
  },
  {
    brandImage: IMAGES.NilePerchFisheriesLtd,
    brandName: 'Nile Perch Fisheries Ltd',
    totalSales: '600,129',
    commissionEarned: '42,009',
    paymentsCompleted: '720,350',
    paymentsDue: '30,140',
    totalOrders: '15',
    totalSale: '1.67M',
  },
  {
    brandImage: IMAGES.OmrosFoods,
    brandName: 'Omros Foods',
    totalSales: '550,948',
    commissionEarned: '55,094',
    paymentsCompleted: '503,897',
    paymentsDue: '47,051',
    totalOrders: '08',
    totalSale: '1.65M',
  },
];
